import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import type { Item, StockLevel, StockMovement, StockMovementKind } from '@iobiz/shared'
import { STOCK_KIND_LABELS, stockMovementInputSchema } from '@iobiz/shared'
import { ApiError } from '../../api/client.js'
import { getStock, recordMovement } from './api.js'

/**
 * Onglet Stock d'un article (ADR-09).
 *
 * ⭐ Aucune correction en place : un mouvement erroné se répare par un
 * mouvement INVERSE, qui laisse une trace. C'est ce qui rend l'inventaire
 * défendable — l'historique montre l'erreur et sa correction, au lieu d'un
 * solde qu'on ne sait plus expliquer.
 *
 * ⚠️ Cet écran a besoin du RÉSEAU. Seuls les niveaux agrégés sont répliqués
 * (§7.5) ; l'historique reste serveur. C'est assumé : on ne consulte pas un
 * historique au comptoir, on le consulte pour comprendre un écart.
 */

const MOTIFS: Record<StockMovementKind, string[]> = {
  in: ['Réception fournisseur', 'Retour client', 'Transfert entrant'],
  out: ['Vente', 'Casse ou perte', 'Transfert sortant', 'Usage interne'],
  adjustment: ['Inventaire', 'Correction de saisie'],
}

export function StockPanel({ storeId, item }: { storeId: string; item: Item }): JSX.Element {
  const [level, setLevel] = useState<StockLevel | null>(null)
  const [movements, setMovements] = useState<StockMovement[]>([])
  const [chargement, setChargement] = useState(true)
  const [erreur, setErreur] = useState<string | null>(null)

  const [kind, setKind] = useState<StockMovementKind>('in')
  const [quantite, setQuantite] = useState('')
  const [motif, setMotif] = useState('')
  const [reference, setReference] = useState('')
  const [envoi, setEnvoi] = useState(false)

  useEffect(() => {
    if (!item.trackStock) {
      setChargement(false)
      return
    }
    setChargement(true)
    getStock(storeId, item.id)
      .then((r) => {
        setLevel(r.level)
        setMovements(r.movements)
        setErreur(null)
      })
      .catch((e: unknown) =>
        setErreur(
          e instanceof ApiError && e.status === 0
            ? 'Historique indisponible hors ligne.'
            : e instanceof Error
              ? e.message
              : 'Chargement impossible',
        ),
      )
      .finally(() => setChargement(false))
  }, [storeId, item.id, item.trackStock])

  if (!item.trackStock) {
    return (
      <Alert severity="info" variant="outlined">
        {item.kind === 'service'
          ? 'Un service ne fait pas l’objet d’un suivi de stock.'
          : 'Le suivi de stock est désactivé pour cet article. Activez-le dans la fiche pour enregistrer des mouvements.'}
      </Alert>
    )
  }

  async function enregistrer(): Promise<void> {
    const parsed = stockMovementInputSchema.safeParse({
      itemId: item.id,
      kind,
      quantity: Number(quantite),
      reason: motif || undefined,
      reference: reference || undefined,
    })
    if (!parsed.success) {
      setErreur(parsed.error.issues[0]?.message ?? 'Saisie invalide')
      return
    }

    setEnvoi(true)
    setErreur(null)
    try {
      const r = await recordMovement(storeId, item.id, parsed.data)
      setLevel(r.level)
      setMovements((m) => [r.movement, ...m])
      setQuantite('')
      setMotif('')
      setReference('')
    } catch (e: unknown) {
      setErreur(e instanceof Error ? e.message : 'Enregistrement impossible')
    } finally {
      setEnvoi(false)
    }
  }

  const quantiteCourante = level?.quantity ?? 0
  const avecAuteur = movements.some((m) => m.createdByName)
  const sousLeSeuil = item.minStockLevel > 0 && quantiteCourante <= item.minStockLevel
  const negatif = quantiteCourante < 0

  return (
    <Box sx={{ display: 'grid', gap: 4, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
      <Paper sx={{ p: 6, borderRadius: 3 }}>
        <Typography variant="h3" sx={{ mb: 4 }}>
          État du stock
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 3, mb: 3 }}>
          <Typography
            className="tabular"
            sx={{ fontSize: 40, fontWeight: 700, color: negatif ? 'error.main' : 'text.primary' }}
          >
            {chargement ? '…' : quantiteCourante}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            en stock
          </Typography>
        </Box>

        {negatif && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Stock négatif. Deux ventes ont pu porter sur le même article, l’une depuis un
            appareil hors ligne. Régularisez par un ajustement d’inventaire.
          </Alert>
        )}
        {!negatif && sousLeSeuil && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Sous le seuil d’alerte ({item.minStockLevel}).
          </Alert>
        )}

        <Typography variant="h3" sx={{ mb: 3, mt: 5 }}>
          Enregistrer un mouvement
        </Typography>

        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={kind}
          onChange={(_, v: StockMovementKind | null) => {
            if (!v) return
            setKind(v)
            setMotif('')
          }}
          sx={{ mb: 3 }}
        >
          <ToggleButton value="in">Entrée</ToggleButton>
          <ToggleButton value="out">Sortie</ToggleButton>
          <ToggleButton value="adjustment">Ajustement</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: '1fr 1fr' }}>
          <TextField
            label="Quantité"
            type="number"
            size="small"
            value={quantite}
            onChange={(e) => setQuantite(e.target.value)}
            helperText={
              kind === 'adjustment'
                ? 'Négatif accepté pour corriger à la baisse'
                : 'Toujours en positif'
            }
          />
          <TextField
            select
            label="Motif"
            size="small"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
          >
            <MenuItem value="">—</MenuItem>
            {MOTIFS[kind].map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Référence"
            size="small"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            helperText="Bon de livraison, facture…"
            sx={{ gridColumn: '1 / -1' }}
          />
        </Box>

        {erreur && (
          <Alert severity="error" sx={{ mt: 3 }} role="alert">
            {erreur}
          </Alert>
        )}

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ mt: 4 }}
          disabled={envoi || quantite === ''}
          onClick={() => void enregistrer()}
        >
          {envoi ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </Paper>

      <Paper sx={{ p: 6, borderRadius: 3 }}>
        <Typography variant="h3" sx={{ mb: 4 }}>
          Historique
        </Typography>

        {movements.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {chargement ? 'Chargement…' : 'Aucun mouvement enregistré.'}
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Nature</TableCell>
                <TableCell align="right">Quantité</TableCell>
                <TableCell>Motif</TableCell>
                {/* « S'il y a lieu » : une colonne vide sur toute la hauteur
                    n'apprend rien et coûte de la largeur. On ne l'affiche donc
                    que si au moins un mouvement porte un auteur — les données
                    d'avant le suivi des auteurs restent lisibles. */}
                {avecAuteur && <TableCell>Par</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {movements.map((m) => (
                <TableRow key={m.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(m.createdAt).toLocaleDateString('fr-FR')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={STOCK_KIND_LABELS[m.kind]}
                      color={m.kind === 'in' ? 'success' : m.kind === 'out' ? 'default' : 'warning'}
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    className="tabular"
                    sx={{ color: m.quantity < 0 ? 'error.main' : 'success.main', fontWeight: 600 }}
                  >
                    {m.quantity > 0 ? `+${m.quantity}` : m.quantity}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    {m.reason ?? '—'}
                    {m.reference && ` · ${m.reference}`}
                  </TableCell>
                  {avecAuteur && (
                    <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {m.createdByName ?? '—'}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  )
}
